import { useLocation, useNavigate } from 'react-router-dom';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { useCallback, useContext, useMemo, useState } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import {
    SensorsAccessoriesPageData,
} from 'src/sensors/accessories/SensorsAccessoriesPageMapping.ts';
import { MainLayout } from 'src/modules/common/layouts/MainLayout.tsx';
import { NavigationContainer } from 'src/modules/common/components/Navigation/NavigationContainer.tsx';
import { Button } from 'src/common/Button/Button.tsx';
import { RuntimeContext } from 'src/runtimeContext';
import { isRecommendedAccessory } from 'src/utils/isRecommendedAccessory.ts';
import { calculateTotals } from 'src/sensors/checkout/CalculateTotals.ts';
import { addToCart } from 'src/utils/addToCart.ts';
import { ValveIcon } from 'src/modules/common/icons/ValveIcon';
import { DeleteIcon } from 'src/modules/common/icons/DeleteIcon';
import { SelectedProducts } from 'src/modules/common/components/SelectedProducts/SelectedProducts';
import { prepareSensorAttributes } from 'src/sensors/list/prepareSensorAttributes.ts';
import { RegionsEnum } from 'src/generated/operations.ts';
import { useImperial } from 'src/sensors/hooks/useImperial';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader';
import { Accessory } from 'src/common/Accessory/Accessory';
import { Device } from 'src/common/Device/Device';
import { Price } from 'src/common/Price/Price';
import { Counter } from 'src/common/Counter/Counter';
import { SubTotal } from 'src/common/SubTotal/SubTotal';
import { Total } from 'src/common/Total/Total';
import { checkoutViewOnly } from 'src/utils/isCheckoutEnabled';
import { InlineNotification } from 'src/modules/common/components/InlineNotification/InlineNotification';
import { useLocationState } from 'src/sensors/useLocationState.ts';
import { SensorBreadcrumbs } from 'src/sensors/common/components/SensorBreadcrumbs/SensorBreadcrumbs';
import { PATHS } from 'src/sensors/common/types/Paths';
import { CartIcon } from 'src/modules/common/icons/CartIcon';
import { useDefaultProject } from 'src/projects/hooks/useDefaultProject';
import { prepareSelectionExport } from 'src/sensors/checkout/prepareSelectionExport.ts';
import { SelectionsButton } from 'src/projects/components/SelectionsButton.tsx';
import { useBelimoTranslate } from 'src/common/hooks/useBelimoTranslate.ts';
import { CheckoutComment } from 'src/common/CheckoutComment/CheckoutComment.tsx';
import { parseRecommended } from 'src/modules/common/utils/parseRecommended.ts';
import { PipeConnectorsView } from 'src/common/PipeConnectorsView/PipeConnectorsView.tsx';
import { EDIT_SELECTION_PARAM } from 'src/common/constants.ts';
import { SaveIcon } from 'src/modules/common/icons/SaveIcon.tsx';
import { AddToListIcon } from 'src/modules/common/icons/AddToListIcon.tsx';

export const SensorsCheckoutPage = ({ data } : {
    data: SensorsAccessoriesPageData;
}) => {
    const translate = useBelimoTranslate();
    const [inProgress, setInProgress] = useState<boolean>(false);
    const [locationState, setLocationState, searchParams] = useLocationState();
    const { userSettings, currency } = useContext(ConfigContext);
    const { isMobile, isRevitPlugin } = useContext(RuntimeContext);
    const { state: { selectionRepository, persistCallback } } = useDefaultProject();

    const navigate = useNavigate();
    const location = useLocation();

    const editSelectionName = searchParams.get(EDIT_SELECTION_PARAM) ?? '';
    const isEditMode = editSelectionName !== '';

    const setSensorQty = useCallback((qty: number) => {
        setLocationState({
            ...locationState,
            qty,
        });
    }, [locationState, setLocationState]);

    const setAccessoryQty = useCallback((name: string, value: number) => {
        setLocationState({
            ...locationState,
            selectedAccessories: locationState.selectedAccessories.map((item) => {
                const { label } = item;
                if (label === name) {
                    return { label, qty: value };
                }
                return item;
            }),
        });
    }, [locationState, setLocationState]);

    const onPipeConnectorChange = useCallback((name: string, value: number) => {
        setLocationState({
            ...locationState,
            pipeConnectors: false,
            selectedAccessories: locationState.selectedAccessories.map((item) => {
                const { label } = item;
                if (label === name) {
                    return { label, qty: value };
                }
                return item;
            }),
        });
    }, [locationState, setLocationState]);

    const deleteAccessory = useCallback((label: string) => {
        setLocationState({
            ...locationState,
            selectedAccessories: locationState.selectedAccessories
                .filter((selectedLabel) => selectedLabel.label !== label),
        });
    }, [locationState, setLocationState]);

    const removePipeConnectorAccessory = useCallback((label: string) => {
        setLocationState({
            ...locationState,
            pipeConnectors: false,
            selectedAccessories: locationState.selectedAccessories
                .filter((selectedLabel) => selectedLabel.label !== label),
        });
    }, [locationState, setLocationState]);

    const checkoutHandler = useCallback(() => {
        if (!data || inProgress) {
            return;
        }
        setInProgress(true);
        const {
            qty,
        } = locationState;
        addToCart([
            {
                label: locationState.sensorLabel,
                qty,
            },
            ...locationState.selectedAccessories,
        ]);
        setLocationState({
            ...locationState,
            sent: true,
        });
        setInProgress(false);
    }, [inProgress, setInProgress, locationState, setLocationState, data]);

    const selectMoreProductsHandler = useCallback(() => {
        navigate({
            pathname: PATHS.Home,
        });
    }, [navigate]);

    const { isImperial } = useImperial();

    const { sensor } = data.accessories;

    const {
        accessories,
        accessoriesSubtotal,
        pipeConnectors,
        pipeConnectorsSubtotal,
        currentCurrency,
        subtotal,
        total,
        pricePerUnit,
    } = useMemo(() => calculateTotals(
        data.accessories,
        userSettings,
        locationState,
        currency,
        locationState.pipeConnectors,
    ), [locationState, currency, data.accessories, userSettings]);

    const exportData = useMemo(
        () => prepareSelectionExport({
            userSettings,
            locationState,
            accessories: [...pipeConnectors, ...accessories],
            sensor,
            isImperial,
        }),
        [pipeConnectors, accessories, isImperial, locationState, sensor, userSettings],
    );

    const isCheckoutViewOnly = checkoutViewOnly(userSettings.shoppingCenter);

    const addToSelection = useCallback(() => {
        if (inProgress) {
            return;
        }
        setInProgress(true);

        // Persisted selections must not carry the transient edit marker in their query.
        const cleanParams = new URLSearchParams(searchParams);
        cleanParams.delete(EDIT_SELECTION_PARAM);
        const searchQuery = cleanParams.toString();

        const existing = isEditMode ? selectionRepository.get(editSelectionName) : null;
        const selectionToSave = {
            type: 'sensors' as const,
            createdAt: existing?.createdAt ?? new Date(),
            updatedAt: new Date(),
            name: isEditMode ? editSelectionName : selectionRepository.proposeName(translate(MESSAGES.Selection)),
            exportData,
            pathname: PATHS.Checkout,
            searchQuery,
            content: [],
            version: '2',
        };

        if (isEditMode) {
            selectionRepository.update(selectionToSave);
        } else {
            selectionRepository.add(selectionToSave);
        }

        persistCallback(selectionRepository.list(selectionRepository.count(), 0)).then(() => {
            navigate({
                pathname: `${location.pathname}/projects/1`,
                search: searchQuery,
            }, {
                state: locationState,
                replace: true,
            });
            // @TODO handle errors
        })
            // eslint-disable-next-line no-console
            .catch((err) => console.error(err))
            .finally(() => setInProgress(false));
    }, [
        translate,
        location,
        inProgress,
        persistCallback,
        selectionRepository,
        locationState,
        exportData,
        searchParams,
        navigate,
        isEditMode,
        editSelectionName,
    ]);

    const removePipeConnectors = useCallback(() => {
        const { isRecommended } = parseRecommended(sensor.connectors);
        setLocationState({
            ...locationState,
            selectedAccessories: locationState.selectedAccessories.filter(({ label }) => !isRecommended(label)),
            pipeConnectors: false,
        });
    }, [locationState, setLocationState, sensor]);

    const onCommentChange = useCallback((checkoutComment: string) => {
        setLocationState({
            ...locationState,
            checkoutComment,
        });
    }, [locationState, setLocationState]);

    const navigation = (
        <NavigationContainer isMobile={isMobile}>
            {!isRevitPlugin && (
                <Button
                    onClick={addToSelection}
                    size={isMobile ? 'md' : 'sm'}
                    kind="ghost-secondary"
                    icon={(
                        <div className="vss-checkout-page__navigation-icon">
                            {isEditMode ? <SaveIcon/> : <AddToListIcon/>}
                        </div>
                    )}
                >
                    <I18n
                        messageId={isEditMode
                            ? MESSAGES.projectPageSaveSelection
                            : MESSAGES.projectPageAddNewSelection}
                        params={isEditMode ? {
                            name: editSelectionName,
                        } : undefined}
                    />
                </Button>
            )}

            <Button
                onClick={checkoutHandler}
                disabled={locationState.sent || isCheckoutViewOnly}
                size={isMobile ? 'md' : 'sm'}
                icon={(
                    <div className="vss-checkout-page__navigation-icon">
                        <CartIcon/>
                    </div>
                )}
            >
                <I18n messageId={isMobile ? MESSAGES.checkoutMobile : MESSAGES.checkout}/>
            </Button>
        </NavigationContainer>
    );

    if (locationState.sent) {
        return (
            <MainLayout>
                <MainLayoutHeader titleId={MESSAGES.selectedProductsTitle} navigation={null} breadcrumbs={null}/>

                <SelectedProducts onButtonClick={selectMoreProductsHandler}/>
            </MainLayout>
        );
    }

    return (
        <div className="vss-sensors">
            <MainLayout>
                <MainLayoutHeader
                    titleId={MESSAGES.checkoutPageTitle}
                    navigation={isMobile ? null : navigation}
                    breadcrumbs={(
                        <SensorBreadcrumbs
                            currentPage={PATHS.Checkout}
                            locationState={locationState}
                            searchParams={searchParams}
                        />
                    )}
                    selectionButton={!isRevitPlugin && (
                        <SelectionsButton
                            isMobile={isMobile}
                            searchParams={searchParams}
                            locationState={locationState}
                        />
                    )}
                />

                {isCheckoutViewOnly && (
                    <InlineNotification
                        type="warning"
                        message={<I18n messageId={MESSAGES.pleaseContact}/>}
                    />
                )}

                <div className="vss-sensors-checkout-page">
                    <h2 className="vss-sensors-checkout-page__title">
                        <I18n messageId={MESSAGES.checkoutSensorsTitle}/>
                    </h2>

                    <Device
                        fullHeight={true}
                        defaultImage={<ValveIcon/>}
                        product={sensor}
                        view={isMobile ? 'mobile' : 'default'}
                        attributes={prepareSensorAttributes(
                            sensor,
                            userSettings.currentRegion === RegionsEnum.Am,
                            isImperial,
                            locationState.selectedFlowRange,
                        )}
                        price={isCheckoutViewOnly ? '\u2014' : (
                            <Price
                                currency={currentCurrency}
                                price={pricePerUnit}
                                locale={userSettings.currentLocale}
                            />
                        )}
                        counter={<Counter value={locationState.qty} onChange={setSensorQty}/>}
                    />

                    {pipeConnectors.length > 0 && (
                        <div className="vss-checkout-page__section">
                            <PipeConnectorsView
                                isSelected={locationState.pipeConnectors}
                                pipeConnectors={pipeConnectors}
                                userSettings={userSettings}
                                currentCurrency={currentCurrency}
                                isCheckoutViewOnly={isCheckoutViewOnly}
                                removePipeConnectors={removePipeConnectors}
                                onDelete={removePipeConnectorAccessory}
                                onChange={onPipeConnectorChange}
                                sensorQty={locationState.qty || 1}
                            />
                        </div>
                    )}

                    <div className="vss-sensors-checkout-page__section">
                        <CheckoutComment
                            labelText={MESSAGES.CheckoutCommentLabel}
                            placeholder={MESSAGES.CheckoutCommentPlaceholder}
                            value={locationState.checkoutComment}
                            onChange={onCommentChange}
                        />
                    </div>

                    <SubTotal
                        isMobile={isMobile}
                        price={isCheckoutViewOnly ? null : subtotal + pipeConnectorsSubtotal}
                        locale={userSettings.currentLocale}
                        currency={currentCurrency}
                    />

                    {accessories.length > 0 && (
                        <div className="vss-sensors-checkout-page__selected-devices-wrapper">
                            <h2 className="vss-checkout-page__subtitle">
                                <I18n messageId={MESSAGES.checkoutAccessoriesTitle}/>
                            </h2>

                            <div className="vss-sensors-checkout-page__selected-devices">
                                {accessories
                                    .map(({ accessory, price, qty }) => {
                                        const { item } = accessory;
                                        return (
                                            <Accessory
                                                chip={isRecommendedAccessory(item.zOption)
                                                    ? <I18n messageId={MESSAGES.recommended}/> : null}
                                                accessory={item}
                                                key={`${item.Label}-${item.ID}`}
                                                price={isCheckoutViewOnly ? '\u2014' : (
                                                    <Price
                                                        price={price}
                                                        currency={currentCurrency}
                                                        locale={userSettings.currentLocale}
                                                    />
                                                )}
                                                counter={(
                                                    <Counter
                                                        value={qty}
                                                        onChange={(value) => setAccessoryQty(item.Label, value)}
                                                    />
                                                )}
                                            >
                                                <div className="vss-sensors-checkout-page__action-wrapper">
                                                    <div className="vss-checkout-page__price-wrapper">
                                                        <Button
                                                            kind="ghost-secondary"
                                                            iconOnly={true}
                                                            onClick={() => deleteAccessory(item.Label)}
                                                        >
                                                            <div className="vss-checkout-page__icon-wrapper">
                                                                <DeleteIcon/>
                                                            </div>
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Accessory>
                                        );
                                    })}
                            </div>

                            <SubTotal
                                isMobile={isMobile}
                                price={isCheckoutViewOnly ? null : accessoriesSubtotal}
                                locale={userSettings.currentLocale}
                                currency={currentCurrency}
                            />

                        </div>
                    )}

                    <Total
                        price={isCheckoutViewOnly ? null : total}
                        locale={userSettings.currentLocale}
                        currency={currentCurrency}
                    />

                    {!isMobile && (
                        <NavigationContainer isMobile={isMobile}>
                            <Button
                                onClick={checkoutHandler}
                                disabled={locationState.sent || isCheckoutViewOnly}
                                size="sm"
                                icon={(
                                    <div className="vss-checkout-page__navigation-icon">
                                        <CartIcon/>
                                    </div>
                                )}
                            >
                                <I18n messageId={isMobile ? MESSAGES.checkoutMobile : MESSAGES.checkout}/>
                            </Button>
                        </NavigationContainer>
                    )}
                </div>
            </MainLayout>

            {isMobile && navigation}
        </div>
    );
};
